import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Strings() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🧵 JavaScript Strings</h1>

      {/* Introduction */}
      <p className="tutorial-intro">
        Strings in JavaScript are sequences of characters used to store and
        manipulate text. You can use either single quotes <code>' '</code>,
        double quotes <code>" "</code>, or backticks <code>` `</code> to create
        them.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <ul style={{ fontSize: "1.1rem", lineHeight: "1.8" }}>
          <li>Strings are written inside quotes.</li>
          <li>
            You can use single (<code>' '</code>) or double (<code>" "</code>)
            quotes.
          </li>
          <li>
            Template literals (<code>` `</code>) allow embedding variables and
            expressions.
          </li>
          <li>Strings are immutable (cannot be changed directly).</li>
        </ul>
      </div>

      {/* Example 1: Creating Strings */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Creating Strings</h2>
        <p>
          Strings can be created using single quotes, double quotes, or
          backticks (template literals).
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let single = 'Hello World';
let double = "JavaScript Strings";
let backtick = \`Template Literal Example\`;

console.log(single);
console.log(double);
console.log(backtick);`}
          />
          <TryItButton
            code={`let single = 'Hello World';
let double = "JavaScript Strings";
let backtick = \`Template Literal Example\`;

console.log(single);
console.log(double);
console.log(backtick);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #2563eb",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Hello World
            <br />
            JavaScript Strings
            <br />
            Template Literal Example
          </pre>
        </div>
      </div>

      {/* Example 2: String Concatenation */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ String Concatenation</h2>
        <p>
          You can join strings using the <code>+</code> operator or template
          literals.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let firstName = "John";
let lastName = "Doe";

console.log("Using + :", firstName + " " + lastName);
console.log(\`Using Template Literal: \${firstName} \${lastName}\`);`}
          />
          <TryItButton
            code={`let firstName = "John";
let lastName = "Doe";

console.log("Using + :", firstName + " " + lastName);
console.log(\`Using Template Literal: \${firstName} \${lastName}\`);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Using + : John Doe
            <br />
            Using Template Literal: John Doe
          </pre>
        </div>
      </div>

      {/* Example 3: String Length */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ String Length</h2>
        <p>
          The <code>.length</code> property gives the total number of characters
          in a string.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "JavaScript";
console.log("Length:", text.length);`}
          />
          <TryItButton
            code={`let text = "JavaScript";
console.log("Length:", text.length);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          Output: <b>Length: 10</b>
        </div>
      </div>

      {/* Example 4: String Methods */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Common String Methods</h2>
        <p>
          JavaScript provides many built-in methods to manipulate strings, such
          as <code>toUpperCase()</code>, <code>toLowerCase()</code>,{" "}
          <code>slice()</code>, <code>replace()</code>, and <code>trim()</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let str = "  JavaScript is Fun!  ";

console.log("Uppercase:", str.toUpperCase());
console.log("Lowercase:", str.toLowerCase());
console.log("Trimmed:", str.trim());
console.log("Slice(2, 11):", str.slice(2, 11));
console.log("Replace:", str.replace("Fun", "Awesome"));`}
          />
          <TryItButton
            code={`let str = "  JavaScript is Fun!  ";

console.log("Uppercase:", str.toUpperCase());
console.log("Lowercase:", str.toLowerCase());
console.log("Trimmed:", str.trim());
console.log("Slice(2, 11):", str.slice(2, 11));
console.log("Replace:", str.replace("Fun", "Awesome"));`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Uppercase: JAVASCRIPT IS FUN!
            <br />
            Lowercase: javascript is fun!
            <br />
            Trimmed: JavaScript is Fun!
            <br />
            Slice(2, 11): JavaScrip
            <br />
            Replace: JavaScript is Awesome!
          </pre>
        </div>
      </div>

      {/* Example 5: Template Literals */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Template Literals</h2>
        <p>
          Template literals (backticks) allow embedding variables and
          expressions directly inside strings.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let name = "Alice";
let age = 25;

console.log(\`My name is \${name} and I am \${age} years old.\`);
console.log(\`Next year, I will be \${age + 1}.\`);`}
          />
          <TryItButton
            code={`let name = "Alice";
let age = 25;

console.log(\`My name is \${name} and I am \${age} years old.\`);
console.log(\`Next year, I will be \${age + 1}.\`);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            My name is Alice and I am 25 years old.
            <br />
            Next year, I will be 26.
          </pre>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>String Searching Methods</b> like{" "}
            <code>indexOf()</code>, <code>includes()</code>, and{" "}
            <code>startsWith()</code>.
          </li>
          <li>
            Explore <b>String Templates</b> for multi-line strings.
          </li>
          <li>
            Practice creating small projects using string manipulation (like
            greeting message generators).
          </li>
        </ul>
      </div>
    </div>
  );
}
